import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import { useOrganization, useSession, useUser } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom';
import { useThread, useThreadRuntime, type ThreadMessageLike } from '@assistant-ui/react';
import type {
  DashboardAssistantDockMessage,
  DashboardAssistantDockSession,
} from '../../../../contexts/DashboardAssistantDockContext';
import {
  appendAssistantEntry,
  cancelAssistantRun,
  getActiveAssistantRun,
  getAssistantRun,
  getAssistantThread,
  isDashboardAssistantHistoryEnabled,
  startAssistantRun,
} from '../../../../api/dashboardAssistantHistory';
import { DASHBOARD_ASSISTANT_CLIENT_CAPABILITIES } from '@charitystack/dashboard-assistant-contracts';
import { isInngestAssistantExecutionEnabled } from '../../../../config/dashboardAssistantExecution';
import type {
  AssistantRuntimeMessageLike,
  DashboardAssistantToolResultEventDetail,
} from '../types';
import { getActiveOrganizationContext } from '../lib/organizationContext';
import {
  assistantEntryToDockMessage,
  cleanAssistantHistoryMessages,
  getFrontendToolEntryKind,
  getFrontendToolEntryRole,
  getMessagesForAgentContext,
  mergeDockMessages,
  mergePersistedHistoryWithLiveMessages,
  normalizeMessageTurnOrder,
  toDockMessages,
} from '../lib/messageModel';
import { getAssistantSendError, getAutoExecutableNavigationPath } from '../lib/backgroundRun';
import { resolveDurableRunCompletion } from '../lib/durableRunCompletion';
import { transientPollDelayMs } from '../lib/transientPollError';
import { settlePendingExclusionWrites } from '../lib/contactSelectionExclusionQueue';

// Poll cadence for the persisted thread history. The backend writes every
// assistant reply to DynamoDB as the AI SDK stream finalizes, so the DB is the
// source of truth. Streaming UI messages are still useful as a short-lived
// fallback while the persisted write catches up.
const HISTORY_POLL_MS = 1500;
const POST_SEND_POLL_MS = 500;
const POST_SEND_POLL_TIMEOUT_MS = 15 * 1000;
const ACTIVE_THREAD_ID_GLOBAL = '__CHARITYSTACK_DASHBOARD_ASSISTANT_THREAD_ID__';
// Safety net: if a send doesn't produce a visible assistant reply within
// this window we stop showing the loading indicator. Prevents a permanent
// spinner if persistence fails silently or the backend takes longer than
// expected.
const LOADING_TIMEOUT_MS = 90 * 1000;
const FINAL_ENTRY_GRACE_MS = 5 * 1000;

function getMessageContentKey(message: DashboardAssistantDockMessage) {
  return `${message.role}:${message.content.trim()}`;
}

function getPersistedBaseline(messages: DashboardAssistantDockMessage[]) {
  const sequences = messages
    .map((message) => message.sequence)
    .filter((sequence): sequence is number => Number.isFinite(sequence));

  return {
    count: messages.length,
    maxSequence: sequences.length > 0 ? Math.max(...sequences) : null,
  };
}

type PersistedBaseline = ReturnType<typeof getPersistedBaseline>;

type WaitingReplyTarget = {
  baseline: PersistedBaseline | null;
  content: string | null;
  startedAt: number | null;
  userId: string | null;
};

function hasNewAssistantMessageAfterBaseline(
  messages: DashboardAssistantDockMessage[],
  baseline: PersistedBaseline,
) {
  if (baseline.maxSequence !== null) {
    return messages.some(
      (message) =>
        isVisibleAssistantMessage(message) &&
        typeof message.sequence === 'number' &&
        message.sequence > baseline.maxSequence!,
    );
  }

  return messages.slice(baseline.count).some(isVisibleAssistantMessage);
}

// Renderers that count as a visible assistant reply on their own (a rich card
// with no accompanying text). Keep in sync with the rich renderers in
// activityHelpers.tsx. Without this, a turn that produces ONLY a card would
// leave the dock waiting/polling forever.
const STANDALONE_CARD_RENDERERS = new Set([
  'dashboard_analytics_result',
  'dashboard_search_results',
  'contact_selection_preview',
  'contact_tags_apply_result',
  'audience_proposal',
]);

function isVisibleAssistantMessage(message: DashboardAssistantDockMessage) {
  return (
    message.role === 'assistant' &&
    ((message.kind === 'message' && message.content.trim().length > 0) ||
      (message.displayPayload?.renderer
        ? STANDALONE_CARD_RENDERERS.has(message.displayPayload.renderer)
        : false))
  );
}

function normalizeWaitingContent(content: string | null) {
  return content?.trim().replace(/\s+/g, ' ') || null;
}

function getMessageTimestampMs(message: DashboardAssistantDockMessage) {
  if (!message.timestamp) {
    return null;
  }

  const timestamp = Date.parse(message.timestamp);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function isWaitingUserMessage(message: DashboardAssistantDockMessage, target: WaitingReplyTarget) {
  if (message.role !== 'user') {
    return false;
  }

  if (target.userId && (message.id === target.userId || message.turnId === target.userId)) {
    return true;
  }

  const targetContent = normalizeWaitingContent(target.content);
  if (!targetContent || normalizeWaitingContent(message.content) !== targetContent) {
    return false;
  }

  if (target.baseline && target.baseline.maxSequence !== null) {
    return typeof message.sequence === 'number' && message.sequence > target.baseline.maxSequence;
  }

  const messageTimestamp = getMessageTimestampMs(message);
  if (target.startedAt && messageTimestamp) {
    return messageTimestamp >= target.startedAt - 5_000;
  }

  return target.baseline === null || target.baseline.count === 0;
}

function hasAssistantAfterWaitingUser(
  messages: DashboardAssistantDockMessage[],
  target: WaitingReplyTarget,
) {
  let userIndex = -1;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (isWaitingUserMessage(messages[index], target)) {
      userIndex = index;
      break;
    }
  }

  return userIndex >= 0 && messages.slice(userIndex + 1).some(isVisibleAssistantMessage);
}

function hasVisibleAssistantReplyForWaitingTarget(
  messages: DashboardAssistantDockMessage[],
  target: WaitingReplyTarget,
) {
  if (target.baseline && hasNewAssistantMessageAfterBaseline(messages, target.baseline)) {
    return true;
  }

  return hasAssistantAfterWaitingUser(messages, target);
}

function getWaitingReplyTarget(
  waitingOnUserIdRef: MutableRefObject<string | null>,
  waitingOnBaselineRef: MutableRefObject<PersistedBaseline | null>,
  waitingOnUserContentRef: MutableRefObject<string | null>,
  waitingStartedAtRef: MutableRefObject<number | null>,
): WaitingReplyTarget | null {
  const target: WaitingReplyTarget = {
    baseline: waitingOnBaselineRef.current,
    content: waitingOnUserContentRef.current,
    startedAt: waitingStartedAtRef.current,
    userId: waitingOnUserIdRef.current,
  };

  if (!target.userId && !target.content && !target.baseline) {
    return null;
  }

  return target;
}

function clearWaitingState(
  waitingOnUserIdRef: MutableRefObject<string | null>,
  waitingOnBaselineRef: MutableRefObject<PersistedBaseline | null>,
  waitingOnUserContentRef: MutableRefObject<string | null>,
  waitingStartedAtRef: MutableRefObject<number | null>,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
) {
  waitingOnUserIdRef.current = null;
  waitingOnBaselineRef.current = null;
  waitingOnUserContentRef.current = null;
  waitingStartedAtRef.current = null;
  setIsLoading(false);
}

function sleep(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function setActiveAssistantThreadId(threadId: string) {
  if (typeof window === 'undefined') {
    return;
  }
  (window as unknown as Record<string, string>)[ACTIVE_THREAD_ID_GLOBAL] = threadId;
}

function toAssistantThreadMessages(
  messages: DashboardAssistantDockMessage[] = [],
): ThreadMessageLike[] {
  return getMessagesForAgentContext(messages)
    .filter((message) => message.content.trim().length > 0)
    .map((message) => ({
      id: message.id || undefined,
      role: message.role === 'user' ? ('user' as const) : ('assistant' as const),
      content: [{ type: 'text' as const, text: message.content }],
      createdAt: message.timestamp ? new Date(message.timestamp) : undefined,
      metadata: {
        custom: {
          turnId: message.turnId,
          runId: message.runId,
        },
      },
    }));
}

export function useDashboardAssistantChat(session: DashboardAssistantDockSession) {
  const history = useHistory();
  const threadRuntime = useThreadRuntime({ optional: true });
  const threadState = useThread({ optional: true });
  const { organization } = useOrganization();
  const { session: clerkSession } = useSession();
  const { user } = useUser();
  const memberships = user?.organizationMemberships || [];
  const { organizationId } = getActiveOrganizationContext({
    memberships,
    organization,
    sessionOrganizationId: clerkSession?.lastActiveOrganizationId,
  });

  // Single source of rendered truth: whatever the backend has persisted, plus
  // optimistic user messages we've added locally but haven't seen come back
  // from the backend yet.
  const [persistedMessages, setPersistedMessages] = useState<DashboardAssistantDockMessage[]>(() =>
    cleanAssistantHistoryMessages(session.historyMessages ?? []),
  );
  const [optimisticMessages, setOptimisticMessages] = useState<DashboardAssistantDockMessage[]>([]);
  const [frontendToolMessages, setFrontendToolMessages] = useState<DashboardAssistantDockMessage[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  // Id of the user message we sent most recently — used to detect when the
  // assistant has replied and clear the loading indicator.
  const waitingOnUserIdRef = useRef<string | null>(null);
  const waitingOnBaselineRef = useRef<PersistedBaseline | null>(null);
  const waitingOnUserContentRef = useRef<string | null>(null);
  const waitingStartedAtRef = useRef<number | null>(null);
  const activeRunIdRef = useRef<string | null>(null);
  const persistedMessagesRef = useRef(persistedMessages);
  const sendInFlightRef = useRef(false);
  const [readyScope, setReadyScope] = useState<string | null>(null);
  const activeSessionIdRef = useRef(session.id);
  const activeOrganizationIdRef = useRef(organizationId);
  const postSendPollGenerationRef = useRef(0);
  const latestNavigationUserIdRef = useRef<string | null>(null);

  useEffect(() => () => {
    // Closing the panel invalidates queued sends and every old continuation.
    activeSessionIdRef.current = '';
    postSendPollGenerationRef.current += 1;
  }, []);

  useEffect(() => {
    setActiveAssistantThreadId(session.id);
  }, [session.id]);

  useEffect(() => {
    const contextChanged =
      activeSessionIdRef.current !== session.id ||
      activeOrganizationIdRef.current !== organizationId;
    activeSessionIdRef.current = session.id;
    activeOrganizationIdRef.current = organizationId;
    if (contextChanged) {
      postSendPollGenerationRef.current += 1;
      latestNavigationUserIdRef.current = null;
      activeRunIdRef.current = null;
      setActiveRunId(null);
      setStatusMessage(null);
      sendInFlightRef.current = false;
      clearWaitingState(
        waitingOnUserIdRef,
        waitingOnBaselineRef,
        waitingOnUserContentRef,
        waitingStartedAtRef,
        setIsLoading,
      );
    }
  }, [organizationId, session.id]);

  // When the user switches between sessions in the dock, reset every piece of
  // local state so the new session starts from a clean slate. The next poll
  // tick will repopulate from the backend.
  useEffect(() => {
    const cleanedHistoryMessages = cleanAssistantHistoryMessages(session.historyMessages ?? []);
    setPersistedMessages(cleanedHistoryMessages);
    setOptimisticMessages([]);
    setFrontendToolMessages([]);
    setIsLoading(false);
    setError(null);
    setStatusMessage(null);
    setActiveRunId(null);
    waitingOnUserIdRef.current = null;
    waitingOnBaselineRef.current = null;
    waitingOnUserContentRef.current = null;
    waitingStartedAtRef.current = null;
    activeRunIdRef.current = null;
    sendInFlightRef.current = false;
    threadRuntime?.reset(toAssistantThreadMessages(cleanedHistoryMessages));
    // `session.id` is the boundary here; including history or runtime object
    // identity would overwrite a live assistant reply while the poller is
    // catching up and can cause repeated state resets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  // Rehydrate the durable thread lock after refreshes and session switches.
  // Keep the composer locked while the check is unavailable so a transient
  // network failure cannot turn into a predictable ACTIVE_RUN conflict.
  useEffect(() => {
    if (!organizationId || !isInngestAssistantExecutionEnabled()) {
      return undefined;
    }

    let cancelled = false;
    let retryId: number | undefined;
    const threadId = session.id;
    setReadyScope(null);
    setIsLoading(true);

    let transientFailures = 0;
    const hydrate = async () => {
      try {
        const activeRun = await getActiveAssistantRun({ threadId, organizationId });
        transientFailures = 0;
        if (
          cancelled ||
          activeSessionIdRef.current !== threadId ||
          activeOrganizationIdRef.current !== organizationId
        ) {
          return;
        }
        setStatusMessage(null);
        setReadyScope(`${organizationId}:${threadId}`);
        if (activeRun) {
          activeRunIdRef.current = activeRun.runId;
          setActiveRunId(activeRun.runId);
          setIsLoading(true);
          return;
        }
        activeRunIdRef.current = null;
        setActiveRunId(null);
        clearWaitingState(
          waitingOnUserIdRef,
          waitingOnBaselineRef,
          waitingOnUserContentRef,
          waitingStartedAtRef,
          setIsLoading,
        );
      } catch (hydrateError) {
        if (cancelled) return;
        const backoff = transientPollDelayMs(hydrateError, transientFailures + 1);
        if (backoff !== null) transientFailures += 1;
        if (backoff === null) {
          console.warn('[DashboardAssistant] Failed to rehydrate active run:', hydrateError);
          setStatusMessage('Reconnecting to the assistant…');
        }
        retryId = window.setTimeout(() => void hydrate(), backoff ?? POST_SEND_POLL_MS);
      }
    };

    void hydrate();
    return () => {
      cancelled = true;
      if (retryId !== undefined) {
        window.clearTimeout(retryId);
      }
    };
  }, [organizationId, session.id]);

  useEffect(() => {
    if (organizationId && !isInngestAssistantExecutionEnabled()) {
      setReadyScope(`${organizationId}:${session.id}`);
    }
  }, [organizationId, session.id]);

  useEffect(() => {
    const handleToolResult = (event: Event) => {
      const detail = (event as CustomEvent<DashboardAssistantToolResultEventDetail>).detail;
      const message = detail?.message;
      if (!message) {
        return;
      }
      if (detail.sessionId && detail.sessionId !== session.id) {
        return;
      }

      const clientEntryId =
        message.id ||
        `frontend-tool-${session.id}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const timestamp = message.timestamp || new Date().toISOString();
      const nextMessage: DashboardAssistantDockMessage = {
        ...message,
        id: clientEntryId,
        timestamp,
      };

      setFrontendToolMessages((current) =>
        normalizeMessageTurnOrder(mergeDockMessages(current, [nextMessage])),
      );

      if (!organizationId || !isDashboardAssistantHistoryEnabled()) {
        return;
      }

      void appendAssistantEntry({
        threadId: session.id,
        clientEntryId,
        organizationId,
        role: getFrontendToolEntryRole(nextMessage),
        kind: getFrontendToolEntryKind(nextMessage),
        content: nextMessage.content || nextMessage.displayPayload?.summary || '',
        turnId: nextMessage.turnId || waitingOnUserIdRef.current || undefined,
        runId: nextMessage.runId,
        parentEntryId: nextMessage.parentEntryId,
        status: nextMessage.status || 'complete',
        displayPayload: nextMessage.displayPayload,
        metadata: {
          ...nextMessage.metadata,
          source: nextMessage.metadata?.source || 'dashboard-assistant-frontend-tool',
        },
        createdAt: timestamp,
      }).catch((appendError) => {
        console.warn('[DashboardAssistant] Failed to persist frontend tool result:', appendError);
      });
    };

    window.addEventListener('dashboard-assistant-tool-result', handleToolResult);
    return () => {
      window.removeEventListener('dashboard-assistant-tool-result', handleToolResult);
    };
  }, [organizationId, session.id]);

  const applyPersistedMessages = (persisted: DashboardAssistantDockMessage[]) => {
    persistedMessagesRef.current = persisted;
    setPersistedMessages(persisted);

    // Drop optimistic messages that have been persisted. Match by id when
    // available, but also by role/content because the runtime may persist the
    // same message under a different entry id.
    setOptimisticMessages((current) => {
      if (current.length === 0) return current;
      const persistedIds = new Set(
        persisted.map((m) => m.id).filter((id): id is string => Boolean(id)),
      );
      const persistedContentKeys = new Set(persisted.map(getMessageContentKey));
      const next = current.filter(
        (m) =>
          !(m.id && persistedIds.has(m.id)) && !persistedContentKeys.has(getMessageContentKey(m)),
      );
      return next.length === current.length ? current : next;
    });

    // The durable Inngest run row owns completion. Action cards and even a
    // correlated final entry remain progress until the run itself succeeds.
    if (activeRunIdRef.current) {
      return;
    }

    const baseline = waitingOnBaselineRef.current;
    if (baseline && hasNewAssistantMessageAfterBaseline(persisted, baseline)) {
      clearWaitingState(
        waitingOnUserIdRef,
        waitingOnBaselineRef,
        waitingOnUserContentRef,
        waitingStartedAtRef,
        setIsLoading,
      );
      return;
    }

    const target = getWaitingReplyTarget(
      waitingOnUserIdRef,
      waitingOnBaselineRef,
      waitingOnUserContentRef,
      waitingStartedAtRef,
    );
    if (target && hasAssistantAfterWaitingUser(persisted, target)) {
      clearWaitingState(
        waitingOnUserIdRef,
        waitingOnBaselineRef,
        waitingOnUserContentRef,
        waitingStartedAtRef,
        setIsLoading,
      );
    }
  };

  // Continuous poll of the persisted thread. DynamoDB remains canonical, but
  // assistant-ui live messages below are merged in as a fallback while the
  // backend write catches up.
  useEffect(() => {
    if (!organizationId || !isDashboardAssistantHistoryEnabled()) {
      return undefined;
    }

    let cancelled = false;
    let inflight = false;
    // After a 429/503, skip interval ticks until the backoff has passed.
    let resumeAt = 0;
    let transientFailures = 0;

    const poll = async () => {
      if (cancelled || inflight || Date.now() < resumeAt) {
        return;
      }
      inflight = true;
      try {
        const threadId = session.id;
        const result = await getAssistantThread({
          threadId,
          organizationId,
          limit: 200,
          allowMissing: true,
        });
        if (
          cancelled ||
          activeSessionIdRef.current !== threadId ||
          activeOrganizationIdRef.current !== organizationId
        ) {
          return;
        }
        transientFailures = 0;
        const persisted = cleanAssistantHistoryMessages(
          result.entries.map(assistantEntryToDockMessage),
        );
        applyPersistedMessages(persisted);
      } catch (err) {
        const backoff = transientPollDelayMs(err, transientFailures + 1);
        if (backoff !== null) {
          transientFailures += 1;
          resumeAt = Date.now() + backoff;
          return;
        }
        const message = err instanceof Error ? err.message : String(err || '');
        // NOT_FOUND is expected for brand-new threads — silent. Other errors
        // log but don't break the poll loop; next interval retries.
        if (!/not found|NOT_FOUND/i.test(message)) {
          console.warn('[DashboardAssistant] Failed to poll thread history:', err);
        }
      } finally {
        inflight = false;
      }
    };

    void poll();
    const intervalId = window.setInterval(poll, HISTORY_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [organizationId, session.id]);

  // assistant-ui retains the old bounded fallback. Durable Inngest runs remain
  // locked and continue polling so a slow run cannot be mistaken for a reply.
  useEffect(() => {
    if (!isLoading) return undefined;
    const timeoutId = window.setTimeout(() => {
      if (activeRunIdRef.current) {
        setStatusMessage('This is taking longer than usual, but the assistant is still working.');
        return;
      }
      clearWaitingState(
        waitingOnUserIdRef,
        waitingOnBaselineRef,
        waitingOnUserContentRef,
        waitingStartedAtRef,
        setIsLoading,
      );
    }, LOADING_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isLoading]);

  const fetchPersistedMessages = async (threadId = session.id) => {
    if (!organizationId || !isDashboardAssistantHistoryEnabled()) {
      return null;
    }

    const result = await getAssistantThread({
      threadId,
      organizationId,
      limit: 200,
      allowMissing: true,
    });
    if (
      activeSessionIdRef.current !== threadId ||
      activeOrganizationIdRef.current !== organizationId
    ) {
      return null;
    }
    return cleanAssistantHistoryMessages(result.entries.map(assistantEntryToDockMessage));
  };

  useEffect(() => {
    if (!activeRunId || !organizationId) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId: number | undefined;
    let succeededWithoutFinalAt: number | null = null;
    let transientFailures = 0;
    const threadId = session.id;

    const finishRun = (nextError?: Error) => {
      if (cancelled || activeRunIdRef.current !== activeRunId) {
        return;
      }
      activeRunIdRef.current = null;
      setActiveRunId(null);
      setStatusMessage(null);
      clearWaitingState(
        waitingOnUserIdRef,
        waitingOnBaselineRef,
        waitingOnUserContentRef,
        waitingStartedAtRef,
        setIsLoading,
      );
      if (nextError) {
        setError(nextError);
      }
    };

    const schedule = (delayMs: number) => {
      timeoutId = window.setTimeout(() => void poll(), delayMs);
    };

    const poll = async () => {
      if (
        cancelled ||
        activeRunIdRef.current !== activeRunId ||
        activeSessionIdRef.current !== threadId ||
        activeOrganizationIdRef.current !== organizationId
      ) {
        return;
      }

      try {
        const [run, persisted] = await Promise.all([
          getAssistantRun({ runId: activeRunId, organizationId }),
          fetchPersistedMessages(threadId),
        ]);
        if (cancelled || activeRunIdRef.current !== activeRunId) {
          return;
        }
        transientFailures = 0;
        if (persisted) {
          applyPersistedMessages(persisted);
        }

        const resolution = resolveDurableRunCompletion(
          run,
          persisted ?? persistedMessagesRef.current,
        );
        if (resolution.state === 'complete') {
          finishRun();
          return;
        }
        if (resolution.state === 'failed') {
          const terminalError = new Error(
            'The assistant could not complete this request. Please try again.',
          );
          terminalError.name = 'AssistantRunTerminalError';
          finishRun(terminalError);
          return;
        }
        if (resolution.state === 'cancelled') {
          const terminalError = new Error('The assistant request was cancelled.');
          terminalError.name = 'AssistantRunTerminalError';
          finishRun(terminalError);
          return;
        }
        if (resolution.state === 'awaiting_final_entry') {
          succeededWithoutFinalAt ??= Date.now();
          if (Date.now() - succeededWithoutFinalAt >= FINAL_ENTRY_GRACE_MS) {
            console.error(
              JSON.stringify({
                source: 'dashboard-assistant-inngest-client',
                event: 'succeededWithoutFinalEntry',
                runId: activeRunId,
              }),
            );
            const invariantError = new Error(
              'The assistant finished, but its final reply could not be loaded. Please try again.',
            );
            invariantError.name = 'AssistantRunInvariantError';
            finishRun(invariantError);
            return;
          }
        } else {
          succeededWithoutFinalAt = null;
        }
      } catch (runError) {
        const backoff = transientPollDelayMs(runError, transientFailures + 1);
        if (backoff !== null) {
          transientFailures += 1;
          schedule(backoff);
          return;
        }
        const message = runError instanceof Error ? runError.message : String(runError || '');
        if (!/not found|NOT_FOUND/i.test(message)) {
          console.warn('[DashboardAssistant] Failed to reconcile asynchronous run:', runError);
        }
      }

      schedule(POST_SEND_POLL_MS);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
    // The active run id is the lifecycle boundary. Poll helpers intentionally
    // read current refs so history updates do not restart this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRunId, organizationId, session.id]);

  const pollForPersistedAssistantReply = async (
    baseline: PersistedBaseline,
    threadId: string,
    pollGeneration: number,
    timeoutMs: number,
    // Ends the wait early, e.g. when the message never reached the runtime.
    shouldStop: () => boolean = () => false,
  ) => {
    const startedAt = Date.now();
    let transientFailures = 0;
    const isStalePoll = () =>
      activeSessionIdRef.current !== threadId ||
      activeOrganizationIdRef.current !== organizationId ||
      postSendPollGenerationRef.current !== pollGeneration;

    while (Date.now() - startedAt < timeoutMs) {
      if (isStalePoll() || shouldStop()) {
        return false;
      }

      let backoff: number | null = null;
      try {
        const persisted = await fetchPersistedMessages(threadId);
        transientFailures = 0;
        if (isStalePoll()) {
          return false;
        }
        if (persisted) {
          const target = getWaitingReplyTarget(
            waitingOnUserIdRef,
            waitingOnBaselineRef,
            waitingOnUserContentRef,
            waitingStartedAtRef,
          );
          applyPersistedMessages(persisted);
          if (
            hasNewAssistantMessageAfterBaseline(persisted, baseline) ||
            (target && hasVisibleAssistantReplyForWaitingTarget(persisted, target))
          ) {
            return true;
          }
        }
      } catch (err) {
        backoff = transientPollDelayMs(err, transientFailures + 1);
        if (backoff !== null) transientFailures += 1;
        const message = err instanceof Error ? err.message : String(err || '');
        if (backoff === null && !/not found|NOT_FOUND/i.test(message)) {
          console.warn('[DashboardAssistant] Failed to fetch post-send thread history:', err);
        }
      }

      await sleep(backoff ?? POST_SEND_POLL_MS);
    }

    return false;
  };

  const liveMessages = useMemo(
    () =>
      normalizeMessageTurnOrder(
        toDockMessages((threadState?.messages ?? []) as unknown as AssistantRuntimeMessageLike[]),
      ),
    [threadState?.messages],
  );

  useEffect(() => {
    if (activeRunIdRef.current) {
      return;
    }
    const target = getWaitingReplyTarget(
      waitingOnUserIdRef,
      waitingOnBaselineRef,
      waitingOnUserContentRef,
      waitingStartedAtRef,
    );
    if (!target || !hasAssistantAfterWaitingUser(liveMessages, target)) {
      return;
    }

    clearWaitingState(
      waitingOnUserIdRef,
      waitingOnBaselineRef,
      waitingOnUserContentRef,
      waitingStartedAtRef,
      setIsLoading,
    );
  }, [liveMessages]);

  const sendMessage = async (content: string): Promise<boolean> => {
    // False when this send was skipped (another is still starting), so the
    // caller keeps the draft instead of clearing it.
    if (activeSessionIdRef.current !== session.id || sendInFlightRef.current || isLoading || activeRunIdRef.current) {
      return false;
    }

    const useInngest = isInngestAssistantExecutionEnabled();
    if (!useInngest && !threadRuntime) {
      throw new Error('Dashboard assistant runtime is not available.');
    }

    sendInFlightRef.current = true;
    // Loading first, so Send stays off (and the draft kept) while this waits.
    setIsLoading(true);
    const threadId = session.id;
    const pollGeneration = postSendPollGenerationRef.current;
    // A contact unchecked just before this turn must be saved before the
    // assistant reads the selection (e.g. to draft a fixed segment).
    await settlePendingExclusionWrites(threadId);
    if (
      activeSessionIdRef.current !== threadId ||
      activeOrganizationIdRef.current !== organizationId ||
      postSendPollGenerationRef.current !== pollGeneration
    ) {
      // The user switched conversation or org while this waited. The context
      // effect already cleared loading and the in-flight flag; send nothing,
      // so the other conversation's view is untouched and the draft stays.
      return false;
    }

    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `message-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    const userMessage: DashboardAssistantDockMessage = {
      id,
      content,
      kind: 'message',
      role: 'user',
      timestamp: new Date().toISOString(),
      turnId: id,
    };

    // assistant-ui's live thread state renders the user message immediately
    // after append. Keeping a separate optimistic copy produces duplicate
    // bubbles because the live message has a runtime-generated id.
    const baseline = getPersistedBaseline(persistedMessages);
    waitingOnUserIdRef.current = id;
    latestNavigationUserIdRef.current = id;
    waitingOnBaselineRef.current = baseline;
    waitingOnUserContentRef.current = content;
    waitingStartedAtRef.current = Date.now();
    setStatusMessage(null);
    setError(null);
    setIsLoading(true);
    if (useInngest) {
      activeRunIdRef.current = 'starting';
      setOptimisticMessages((current) =>
        normalizeMessageTurnOrder(mergeDockMessages(current, [userMessage])),
      );
    }

    // Set when assistant-ui rejects the append: the message never went out, so
    // the send reports false (a "Use this list" click is offered again).
    let appendFailed = false;
    try {
      setActiveAssistantThreadId(threadId);
      if (useInngest) {
        if (!organizationId) {
          throw new Error('An active organization is required to use the dashboard assistant.');
        }
        const startedRun = await startAssistantRun({
          threadId,
          clientEntryId: id,
          organizationId,
          content,
          context: {
            currentFeature: history.location.pathname.split('/').filter(Boolean)[0] || 'home',
            pathname: history.location.pathname,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            // What this dock can render (ADM-223): the assistant sends a keep card only to a dock that reads it.
            clientCapabilities: DASHBOARD_ASSISTANT_CLIENT_CAPABILITIES,
          },
        });
        if (
          activeSessionIdRef.current !== threadId ||
          activeOrganizationIdRef.current !== organizationId ||
          postSendPollGenerationRef.current !== pollGeneration
        ) return true;
        activeRunIdRef.current = startedRun.run.runId;
        setActiveRunId(startedRun.run.runId);
        return true;
      } else {
        // Seed assistant-ui's thread with authoritative persisted history before
        // appending the new user message. Without this the backend only sees the
        // new message and loses multi-turn context.
        threadRuntime!.reset(toAssistantThreadMessages(persistedMessages));
        const appendResult = threadRuntime!.append({
          role: 'user',
          content: [{ type: 'text', text: content }],
          createdAt: new Date(userMessage.timestamp || Date.now()),
          metadata: {
            custom: {
              clientMessageId: id,
              threadId,
            },
          },
        }) as unknown;
        if (
          appendResult &&
          typeof (appendResult as unknown as PromiseLike<void>).then === 'function'
        ) {
          void Promise.resolve(appendResult).catch((appendError) => {
            appendFailed = true;
            if (
              activeSessionIdRef.current !== threadId ||
              activeOrganizationIdRef.current !== organizationId ||
              postSendPollGenerationRef.current !== pollGeneration
            ) {
              return;
            }
            clearWaitingState(
              waitingOnUserIdRef,
              waitingOnBaselineRef,
              waitingOnUserContentRef,
              waitingStartedAtRef,
              setIsLoading,
            );
            setError(getAssistantSendError(appendError));
          });
        }
      }
      const foundPersistedReply = await pollForPersistedAssistantReply(
        baseline,
        threadId,
        pollGeneration,
        useInngest ? LOADING_TIMEOUT_MS : POST_SEND_POLL_TIMEOUT_MS,
        () => appendFailed,
      );
      if (
        activeSessionIdRef.current !== threadId ||
        activeOrganizationIdRef.current !== organizationId ||
        postSendPollGenerationRef.current !== pollGeneration
      ) {
        return true;
      }
      const target = getWaitingReplyTarget(
        waitingOnUserIdRef,
        waitingOnBaselineRef,
        waitingOnUserContentRef,
        waitingStartedAtRef,
      );
      if (
        !foundPersistedReply &&
        (!target || !hasVisibleAssistantReplyForWaitingTarget(liveMessages, target))
      ) {
        clearWaitingState(
          waitingOnUserIdRef,
          waitingOnBaselineRef,
          waitingOnUserContentRef,
          waitingStartedAtRef,
          setIsLoading,
        );
      }
      if (appendFailed) return false;
    } catch (sendError) {
      if (
        activeSessionIdRef.current !== threadId ||
        activeOrganizationIdRef.current !== organizationId ||
        postSendPollGenerationRef.current !== pollGeneration
      ) {
        return true;
      }
      clearWaitingState(
        waitingOnUserIdRef,
        waitingOnBaselineRef,
        waitingOnUserContentRef,
        waitingStartedAtRef,
        setIsLoading,
      );
      if (useInngest) {
        activeRunIdRef.current = null;
        setActiveRunId(null);
        setOptimisticMessages((current) => current.filter((message) => message.id !== id));
      }
      const nextError = getAssistantSendError(sendError);
      setError(nextError);
      throw nextError;
    } finally {
      if (
        activeSessionIdRef.current === threadId &&
        activeOrganizationIdRef.current === organizationId &&
        postSendPollGenerationRef.current === pollGeneration
      ) {
        sendInFlightRef.current = false;
      }
    }
    return true;
  };

  const cancelActiveRun = async () => {
    const runId = activeRunIdRef.current;
    if (!runId || runId === 'starting' || !organizationId) {
      return;
    }
    setStatusMessage('Cancelling this assistant request…');
    try {
      await cancelAssistantRun({ runId, organizationId });
    } catch (cancelError) {
      const nextError = getAssistantSendError(cancelError);
      setError(nextError);
      setStatusMessage(null);
      throw nextError;
    }
  };

  const canCancelActiveRun = Boolean(organizationId && activeRunId && activeRunId !== 'starting');

  // What actually renders: persisted history is canonical; assistant-ui live
  // messages and optimistic user messages fill the gap until persistence lands.
  const displayedMessages = useMemo(
    () =>
      normalizeMessageTurnOrder(
        mergePersistedHistoryWithLiveMessages(
          persistedMessages,
          mergeDockMessages(
            mergeDockMessages(liveMessages, optimisticMessages),
            frontendToolMessages,
          ),
        ),
      ),
    [persistedMessages, liveMessages, optimisticMessages, frontendToolMessages],
  );

  useEffect(() => {
    if (!isLoading || activeRunIdRef.current) {
      return;
    }

    const target = getWaitingReplyTarget(
      waitingOnUserIdRef,
      waitingOnBaselineRef,
      waitingOnUserContentRef,
      waitingStartedAtRef,
    );
    if (!target || !hasVisibleAssistantReplyForWaitingTarget(displayedMessages, target)) {
      return;
    }

    clearWaitingState(
      waitingOnUserIdRef,
      waitingOnBaselineRef,
      waitingOnUserContentRef,
      waitingStartedAtRef,
      setIsLoading,
    );
  }, [displayedMessages, isLoading]);

  // Auto-navigate if the latest persisted assistant message includes a
  // navigation tool result for our last send.
  useEffect(() => {
    const waitingId = latestNavigationUserIdRef.current;
    if (!waitingId) return;
    const navigationPath = getAutoExecutableNavigationPath(displayedMessages, waitingId);
    if (navigationPath) {
      latestNavigationUserIdRef.current = null;
      history.push(navigationPath);
    }
  }, [displayedMessages, history]);

  return {
    canCancelActiveRun,
    conversationId: session.conversationId || session.id,
    cancelActiveRun,
    error,
    // `hasLiveMessages` was used by the panel to decide whether to merge
    // optimistic with persisted history. With this hook owning both halves
    // there is always something to merge, so return true unconditionally.
    hasLiveMessages: true,
    isReady: readyScope === `${organizationId}:${session.id}` && Boolean(organizationId) && (isInngestAssistantExecutionEnabled() || Boolean(threadRuntime)),
    isLoading,
    messages: displayedMessages,
    sendMessage,
    statusMessage,
  };
}
